// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where the machine is standing, and how to get between that and the room (M5-15, M5-16).
//
// One transform: a position on the floor, a yaw, and a scale. No pitch and no roll, deliberately
// — a pinball machine stands on four legs on a level floor, and a machine that can be tilted in
// the room is a machine a player can accidentally place at an angle they cannot correct without
// starting again. Corner handles rotate and resize; nothing tips it over.
//
// **Positions scale and velocities do not, and that is the load-bearing decision here.** In
// tabletop mode the cabinet is 0.42 scale (06 §3), so a hand has to be 42 % as far from the
// centre to touch the side panel — the contact geometry must scale, or a tabletop machine would
// be shoved by hands nowhere near it. But the *strength* of a shove has to stay in real-world
// metres per second, because the ball inside is a full-size steel ball obeying full-size physics.
// Scaling the velocity too would make a tabletop machine 2.4 times as easy to nudge, which is a
// difficulty setting nobody chose and a leaderboard nobody can compare.

/** The machine's pose in the room. Cabinet-local metres times `scale` are room metres. */
export interface Placement {
  /** The cabinet origin — floor level, centre of the footprint — in reference-space metres. */
  readonly x: number
  readonly y: number
  readonly z: number
  /**
   * Rotation about the room's up axis, radians.
   *
   * **The cabinet's front is its local +Z** (`cabinetControls.ts` puts the lockdown bar there),
   * so at yaw 0 the front faces world +Z — which is toward a player standing at the origin with
   * the machine in front of them at negative Z. The front direction at yaw θ is
   * `(−sin θ, cos θ)`, and `yawFacing` below is the inverse. Writing both down is worth the
   * lines: getting this backwards puts the backbox in the player's face, and it reads as a
   * placement bug rather than as a sign error.
   */
  readonly yaw: number
  /** 1 for Full, 0.30–0.60 for Tabletop. */
  readonly scale: number
}

/** A mutable point, for transforms that must not allocate in a frame callback. */
export interface MutablePoint {
  x: number
  y: number
  z: number
}

/** The two scales 06 §3 offers. */
export type PlacementScale = 'full' | 'tabletop'

/** Tabletop's default and the range the corner handles allow (06 §3). */
export const TABLETOP_DEFAULT = 0.42
export const TABLETOP_MIN = 0.30
export const TABLETOP_MAX = 0.60

/** Clamp a scale to what the mode allows. */
export function clampScale(mode: PlacementScale, scale: number): number {
  if (mode === 'full') return 1
  if (!Number.isFinite(scale)) return TABLETOP_DEFAULT
  return scale < TABLETOP_MIN ? TABLETOP_MIN : scale > TABLETOP_MAX ? TABLETOP_MAX : scale
}

/**
 * Room point → cabinet-local metres.
 *
 * Rotate by −yaw about the room's up axis, then divide by the scale. Written out rather than
 * built on a matrix library because it runs four times a frame per hand and because the inverse
 * of a translate-rotate-scale is the one transform people reliably get backwards.
 */
export function toCabinetPoint(
  placement: Placement,
  wx: number,
  wy: number,
  wz: number,
  out: MutablePoint,
): MutablePoint {
  const dx = wx - placement.x
  const dy = wy - placement.y
  const dz = wz - placement.z
  const c = Math.cos(-placement.yaw)
  const s = Math.sin(-placement.yaw)
  const inv = 1 / placement.scale
  out.x = (dx * c - dz * s) * inv
  out.y = dy * inv
  out.z = (dx * s + dz * c) * inv
  return out
}

/**
 * Room direction → cabinet orientation, **without** the scale.
 *
 * For velocities. See the note at the top: the geometry scales and the physics does not.
 */
export function toCabinetDirection(
  placement: Placement,
  wx: number,
  wy: number,
  wz: number,
  out: MutablePoint,
): MutablePoint {
  const c = Math.cos(-placement.yaw)
  const s = Math.sin(-placement.yaw)
  out.x = wx * c - wz * s
  out.y = wy
  out.z = wx * s + wz * c
  return out
}

/** Cabinet-local metres → room point. The forward transform, for drawing. */
export function toRoomPoint(
  placement: Placement,
  cx: number,
  cy: number,
  cz: number,
  out: MutablePoint,
): MutablePoint {
  const sx = cx * placement.scale
  const sy = cy * placement.scale
  const sz = cz * placement.scale
  const c = Math.cos(placement.yaw)
  const s = Math.sin(placement.yaw)
  out.x = placement.x + (sx * c - sz * s)
  out.y = placement.y + sy
  out.z = placement.z + (sx * s + sz * c)
  return out
}

/**
 * The yaw that turns a machine at `(mx, mz)` to face a point at `(tx, tz)`.
 *
 * The inverse of the front vector in `Placement.yaw`. Returns the current yaw when the two
 * points coincide, because a machine standing exactly where the player is has no direction to
 * face and spinning it to zero would be worse than leaving it.
 */
export function yawFacing(mx: number, mz: number, tx: number, tz: number, fallback = 0): number {
  const dx = tx - mx
  const dz = tz - mz
  if (dx === 0 && dz === 0) return fallback
  return Math.atan2(-dx, dz)
}

/**
 * A placement that puts the machine in front of a player standing at the origin.
 *
 * The safe fallback (06 §3): used when an anchor cannot be re-localised, when the room-fit check
 * has nothing to say, and as the starting pose for the placement reticle. 1.1 m away, because
 * that is arm's length plus half a cabinet — close enough to reach the flipper buttons without
 * stepping forward, far enough that the backbox is not in the player's face.
 */
export function defaultPlacement(scale = 1): Placement {
  return { x: 0, y: 0, z: -1.1, yaw: 0, scale }
}
